// src/components/admin/AdminFinances.jsx
// Chiffre d'affaires, bénéfice, frais Stripe, solde Stripe, détail par mois.

import React, { useEffect, useState } from "react";
import { Wallet, TrendingUp, CreditCard, Banknote, ExternalLink, RefreshCw } from "lucide-react";
import { getFinances, synchroniserFinances } from "../../services/AdminGestionService";
import { CarteKpi, EnTete, TableAdmin, BoutonActualiser, Badge, euros } from "./AdminUI";

const nomMois = (m) => {
    const [a, mo] = m.split("-");
    return new Date(a, mo - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};

const AdminFinances = () => {
    const [d, setD] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [synchro, setSynchro] = useState(false);
    const [message, setMessage] = useState(null);

    const charger = () => getFinances().then(setD).catch((e) => setErreur(e.message));
    useEffect(() => { charger(); }, []);

    const synchroniser = async () => {
        setSynchro(true);
        try {
            const r = await synchroniserFinances();
            setMessage(r.message);
            charger();
        } catch (e) {
            setMessage(e.message);
        } finally {
            setSynchro(false);
        }
    };

    if (erreur) return <p className="text-red-500">{erreur}</p>;
    if (!d) return <p className="text-gray-400">Chargement…</p>;
    const r = d.resume;
    const dashStripe = d.mode_test ? "https://dashboard.stripe.com/test" : "https://dashboard.stripe.com";

    const colonnes = [
        { cle: "mois", titre: "Mois", rendu: (m) => <span className="capitalize font-semibold">{nomMois(m.mois)}</span> },
        { cle: "nb", titre: "Paiements", classe: "text-center" },
        { cle: "ca", titre: "Encaissé", classe: "text-right", rendu: (m) => euros(m.ca) },
        { cle: "organisateurs", titre: "Organisateurs", classe: "text-right", rendu: (m) => euros(m.organisateurs) },
        { cle: "stripe", titre: "Frais Stripe", classe: "text-right", rendu: (m) => <span className="text-gray-500">− {euros(m.stripe)}</span> },
        { cle: "commission", titre: "Commission", classe: "text-right", rendu: (m) => <b className="text-green-700">{euros(m.commission)}</b> },
    ];

    return (
        <div className="max-w-7xl mx-auto">
            <EnTete titre="Chiffre d'affaires" sousTitre={d.mode_test ? "⚠️ Stripe en mode TEST" : "Stripe en production"}
                actions={<>
                    <a href={`${dashStripe}/payments`} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-600 hover:bg-gray-50">
                        <ExternalLink size={13} /> Dashboard Stripe
                    </a>
                    <BoutonActualiser onClick={charger} />
                </>}
            />

            {r.a_synchroniser > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-800 text-sm">
                    <span className="flex-1">{r.a_synchroniser} paiement(s) sans montant ou frais Stripe enregistrés (anciens billets).</span>
                    <button onClick={synchroniser} disabled={synchro}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold disabled:opacity-50">
                        <RefreshCw size={13} className={synchro ? "animate-spin" : ""} /> Synchroniser avec Stripe
                    </button>
                </div>
            )}
            {message && <div className="mb-4 px-4 py-2.5 rounded-xl text-sm bg-gray-50 border border-gray-200 text-gray-700">{message}</div>}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                <CarteKpi label="Chiffre d'affaires" valeur={euros(r.chiffre_affaires)} sous="total payé par les acheteurs" icone={Wallet} />
                <CarteKpi label="Commission Thrundrz" valeur={euros(r.commission)} sous="frais de service − frais Stripe" icone={TrendingUp} />
                <CarteKpi label="Bénéfice net" valeur={euros(r.benefice_net)} couleur="text-green-600" sous="après coût des annulations" icone={TrendingUp} />
                <CarteKpi label="Frais Stripe payés" valeur={euros(r.frais_stripe)} couleur="text-gray-600" icone={CreditCard} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <CarteKpi label="Part des organisateurs" valeur={euros(r.part_organisateurs)} icone={Banknote} />
                <CarteKpi label="Déjà versé (RIB)" valeur={euros(r.deja_verse)} couleur="text-green-700" />
                <CarteKpi label="À verser" valeur={euros(r.a_verser)} couleur="text-amber-600"
                    sous={r.versements_echec > 0 ? `${r.versements_echec} en échec` : null} />
                <CarteKpi label="Remboursé aux acheteurs" valeur={euros(r.rembourse)} couleur="text-red-600"
                    sous={`coût pour Thrundrz : ${euros(r.cout_annulations)}`} />
            </div>

            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <h3 className="text-sm font-bold text-gray-900">Solde du compte Stripe Thrundrz</h3>
                    <a href={`${dashStripe}/balance/overview`} target="_blank" rel="noreferrer" className="text-xs text-violet-700 font-semibold hover:underline">
                        Voir les virements Stripe →
                    </a>
                </div>
                {d.solde_stripe ? (
                    <div className="flex flex-wrap gap-6">
                        <div><p className="text-xs text-gray-500">Disponible</p><p className="text-xl font-extrabold text-gray-900">{euros(d.solde_stripe.disponible)}</p></div>
                        <div><p className="text-xs text-gray-500">En attente</p><p className="text-xl font-extrabold text-gray-500">{euros(d.solde_stripe.en_attente)}</p></div>
                    </div>
                ) : <Badge couleur="orange">Solde Stripe indisponible</Badge>}
                <p className="text-[11px] text-gray-400 mt-2">
                    Ce solde contient vos commissions. La part des organisateurs est déjà sur leurs propres comptes Stripe.
                </p>
            </section>

            <h3 className="text-sm font-bold text-gray-900 mb-3">Détail par mois (12 derniers mois)</h3>
            <TableAdmin colonnes={colonnes} lignes={d.par_mois.map((m) => ({ ...m, id: m.mois }))} vide="Aucun paiement." />
        </div>
    );
};

export default AdminFinances;